import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  ShoppingBag,
  CheckCircle2,
  Truck,
  Clock,
  Package,
  Check,
  Tag,
  CreditCard,
  Copy,
  RotateCcw
} from 'lucide-react';

import { getProductImageUrl, BACKEND_URL, createStripeCheckoutSessionBackend } from '../services/api';

const UserOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);

  const handlePayOrder = async () => {
    if (!order) return;
    try {
      setPaying(true);
      const res = await createStripeCheckoutSessionBackend(order.id);
      const sessionUrl = res?.url || res?.data?.session_url || res?.session_url;
      if (res && res.success && sessionUrl) {
        window.location.href = sessionUrl;
      } else {
        alert(res?.message || "Failed to initialize Stripe checkout. Please try again.");
      }
    } catch (err) {
      alert("Error connecting to payment gateway.");
    } finally {
      setPaying(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('userToken') || localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/orders`, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && isMounted && Array.isArray(data.orders)) {
            const found = data.orders.find(o => String(o.id) === String(id) || o.order_number === `#${id}` || o.order_number === id);
            if (found) {
              const d = new Date(found.created_at || Date.now());
              const itemsList = Array.isArray(found.items) ? found.items : [];
              const firstImg = getProductImageUrl(itemsList[0]?.image_url || itemsList[0]?.image || '/uploads/panel_72led.jpg');

              setOrder({
                ...found,
                order_number: found.order_number ? found.order_number : `#IR${found.id}`,
                date_formatted: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                time_formatted: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
                subtotal: Number(found.subtotal || found.total_amount || 0),
                shipping_fee: Number(found.shipping_fee || 0),
                discount_amount: Number(found.discount_amount || 0),
                tax_amount: Number(found.tax_amount || 0),
                total_amount: Number(found.total_amount || 0),
                items_count: itemsList.length,
                payment_method_info: `Paid via ${found.payment_method || 'Visa ending in 4242'}`,
                items: itemsList.map(it => ({
                  name: it.name || 'InfraEdge Panel',
                  specs: '660nm + 850nm • Dual Chip',
                  quantity: it.quantity || 1,
                  price: Number(it.price || 0),
                  selected_addons: Array.isArray(it.selected_addons) ? it.selected_addons : [],
                  image: getProductImageUrl(it.image_url || it.image || firstImg)
                }))
              });
            }
          }
        }
      } catch (e) {
        console.warn("Failed to fetch order details:", e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOrderDetails();
    return () => { isMounted = false; };
  }, [id]);

  if (loading) {
    return (
      <div style={{ minHeight: '80vh', background: '#fafbfc', padding: '60px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '15px', color: '#64748b', fontWeight: 700 }}>Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ minHeight: '80vh', background: '#fafbfc', padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '480px', margin: '0 auto', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '40px' }}>
          <ShoppingBag size={48} color="#dc2626" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: '0 0 8px' }}>Order Not Found</h2>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px' }}>We couldn't find the requested order in your account history.</p>
          <button onClick={() => navigate('/orders')} className="btn-secondary" style={{ width: '100%', padding: '12px', borderRadius: '12px', fontWeight: 800, cursor: 'pointer' }}>
            Back to My Orders
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = (order.order_status || 'Processing').toLowerCase();
  const isUnpaid = order.payment_status !== 'Paid' || currentStatus === 'pending payment' || currentStatus === 'order not placed';
  const refundInfo = order.refund || null;
  const isCancelled = currentStatus === 'cancelled';
  const canCancel = !isCancelled && currentStatus !== 'delivered' && !isUnpaid;

  const handleCancelOrder = async () => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    try {
      setCancelling(true);
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/orders/${order.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ reason: "Customer requested cancellation before delivery" })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert("Order cancelled successfully. Refund request has been created.");
        window.location.reload();
      } else {
        alert(data.message || "Failed to cancel order.");
      }
    } catch (e) {
      alert("Error cancelling order.");
    } finally {
      setCancelling(false);
    }
  };

  // TIMELINE STEPS COMPUTATION FOR REAL HORIZONTAL PROGRESS TRACKER
  // Steps: Order Placed -> Confirmed -> Shipped -> Out for Delivery -> Delivered
  const history = Array.isArray(order.status_history) ? order.status_history : [];

  const getStepTimestamp = (stepName) => {
    const sName = stepName.toLowerCase();
    if (sName === 'order placed') return `${order.date_formatted}, ${order.time_formatted}`;

    const match = history.find(h => (h.status || '').toLowerCase() === sName || (sName === 'confirmed' && h.status === 'Processing'));
    if (match && match.timestamp) {
      const hd = new Date(match.timestamp);
      return `${hd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${hd.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }
    return '--';
  };

  const isStepCompleted = (stepIndex) => {
    // 0: Placed, 1: Confirmed, 2: Shipped, 3: Out for Delivery, 4: Delivered
    if (isUnpaid) return false;
    if (currentStatus === 'cancelled') return false;
    if (currentStatus === 'delivered') return true;
    if (currentStatus === 'shipped') return stepIndex <= 2;
    if (currentStatus === 'processing') return stepIndex <= 1;
    return stepIndex === 0;
  };

  const steps = [
    { title: 'Order Placed', time: getStepTimestamp('order placed'), icon: ShoppingBag },
    { title: 'Confirmed', time: getStepTimestamp('confirmed'), icon: Check },
    { title: 'Shipped', time: getStepTimestamp('shipped'), icon: Truck },
    { title: 'Out for Delivery', time: getStepTimestamp('out for delivery'), icon: Package },
    { title: 'Delivered', time: getStepTimestamp('delivered'), icon: CheckCircle2 }
  ];

  const address = order.shipping_address || {};
  const recipientName = address.full_name || address.name || order.user_name || 'Valued Customer';
  const street = address.street_address || address.street || 'Street Address N/A';
  const city = address.city || 'City';
  const postcode = address.postal_code || address.postcode || '';
  const country = address.country || 'United Kingdom';
  const phone = address.phone || order.guest_phone || '';

  const getStatusBanner = () => {
    if (isUnpaid) {
      return {
        bg: '#fff7ed',
        border: '#fed7aa',
        color: '#c2410c',
        subColor: '#9a3412',
        title: 'Order Not Placed — Payment Incomplete',
        subtext: 'Payment was not received for this checkout session. Your order has not been placed and no items will be dispatched until payment is completed.'
      };
    }
    if (currentStatus === 'delivered') {
      return {
        bg: '#f0fdf4',
        border: '#bbf7d0',
        color: '#15803d',
        subColor: '#166534',
        title: 'Your order has been delivered successfully.',
        subtext: `Delivered on ${order.date_formatted} at ${order.time_formatted} at ${city}, ${country}`
      };
    }
    if (currentStatus === 'shipped') {
      return {
        bg: '#eff6ff',
        border: '#bfdbfe',
        color: '#1d4ed8',
        subColor: '#1e40af',
        title: 'Your order has been shipped and is on its way.',
        subtext: `Shipped on ${getStepTimestamp('shipped') !== '--' ? getStepTimestamp('shipped') : order.date_formatted}`
      };
    }
    if (currentStatus === 'cancelled') {
      return {
        bg: '#fef2f2',
        border: '#fecaca',
        color: '#b91c1c',
        subColor: '#991b1b',
        title: 'Your order has been cancelled.',
        subtext: `Cancelled on ${order.date_formatted}`
      };
    }
    // Processing
    return {
      bg: '#fff7ed',
      border: '#ffedd5',
      color: '#c2410c',
      subColor: '#9a3412',
      title: 'Your order is currently processing.',
      subtext: `We are preparing your items for dispatch.`
    };
  };

  const banner = getStatusBanner();

  return (
    <div className="user-order-detail-container" style={{ minHeight: '100vh', background: '#fafbfc', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1120px', margin: '0 auto', width: '100%' }}>
        
        {/* BACK BUTTON TO MY ORDERS */}
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => navigate('/orders')}
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 800,
              color: '#0f172a',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
            }}
          >
            <ArrowLeft size={16} color="#dc2626" />
            <span>Back to My Orders</span>
          </button>
        </div>

        {/* HEADER BAR MATCHING orderdetail.png 1:1 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
                Order {order.order_number}
              </h1>
              <span style={{ fontSize: '11px', fontWeight: 800, color: isUnpaid ? '#c2410c' : isCancelled ? '#b91c1c' : '#16a34a', background: isUnpaid ? '#fff7ed' : isCancelled ? '#fef2f2' : '#dcfce7', border: `1px solid ${isUnpaid ? '#fed7aa' : isCancelled ? '#fecaca' : '#bbf7d0'}`, padding: '3px 10px', borderRadius: '9999px', textTransform: 'capitalize' }}>
                {isUnpaid ? 'Order Not Placed' : (order.order_status || 'Processing')}
              </span>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
              {isUnpaid ? `Checkout started on ${order.date_formatted} at ${order.time_formatted} (Payment Pending)` : `Placed on ${order.date_formatted} at ${order.time_formatted}`}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {isUnpaid && (
              <button
                disabled={paying}
                onClick={handlePayOrder}
                style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: 800,
                  cursor: paying ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.35)'
                }}
              >
                <span>{paying ? 'Connecting to Stripe...' : 'Complete Payment'}</span>
                <span>➔</span>
              </button>
            )}

            {canCancel && (
              <button
                disabled={cancelling}
                onClick={handleCancelOrder}
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: 800,
                  color: '#dc2626',
                  cursor: cancelling ? 'not-allowed' : 'pointer'
                }}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            )}

            {!isUnpaid && (
              <button
                onClick={() => {
                  const token = localStorage.getItem('userToken') || localStorage.getItem('token');
                  const invoiceUrl = `${BACKEND_URL}/api/orders/${order.id}/invoice`;
                  
                  // Fetch PDF blob or open in new window
                  fetch(invoiceUrl, {
                    headers: { 'Authorization': token ? `Bearer ${token}` : '' }
                  })
                  .then(res => res.blob())
                  .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Invoice_${order.order_number.replace('#', '')}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  })
                  .catch(err => alert("Failed to download PDF invoice."));
                }}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: 800,
                  color: '#0f172a',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                }}
              >
                <Download size={15} color="#475569" />
                <span>Download Invoice</span>
              </button>
            )}
          </div>
        </div>

        {/* UNPAID ORDER CALLOUT ALERT */}
        {isUnpaid && (
          <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '16px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', marginBottom: '24px' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 900, color: '#9a3412', marginBottom: '4px' }}>
                Payment Incomplete — Order Not Placed
              </div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                As this is a prepaid store, your order will not be confirmed or dispatched until payment is received.
              </div>
            </div>
            <button
              disabled={paying}
              onClick={handlePayOrder}
              style={{
                background: '#dc2626',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                padding: '12px 24px',
                fontSize: '13.5px',
                fontWeight: 800,
                cursor: paying ? 'not-allowed' : 'pointer',
                opacity: paying ? 0.7 : 1,
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.35)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{paying ? 'Connecting to Stripe...' : `Pay Now (£${order.total_amount?.toFixed(2)})`}</span>
              <span>➔</span>
            </button>
          </div>
        )}

        {/* 2-COLUMN MAIN GRID: LEFT (65%) vs RIGHT (35%) */}
        <div className="user-order-detail-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px' }}>
          
          {/* LEFT COLUMN: TRACKING, ITEMS, DELIVERY */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0 }}>
            
            {/* 1. ORDER TRACKING HORIZONTAL TIMELINE CARD */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 28px 0' }}>
                Order Tracking
              </h3>

              {/* HORIZONTAL STEPPER WITH MOBILE TOUCH SCROLL */}
              <div className="tracking-stepper-wrapper" style={{ overflowX: 'auto', paddingBottom: '12px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', minWidth: '580px', padding: '0 10px' }}>
                  
                  {/* Connecting Progress Bar Line */}
                  <div style={{ position: 'absolute', top: '18px', left: '40px', right: '40px', height: '2px', background: '#e2e8f0', zIndex: 1 }}>
                    <div style={{
                      height: '100%',
                      background: isCancelled ? '#ef4444' : '#16a34a',
                      width: isCancelled ? '100%' : currentStatus === 'delivered' ? '100%' : currentStatus === 'shipped' ? '50%' : '25%',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>

                  {steps.map((st, idx) => {
                    const done = isStepCompleted(idx);
                    const isLastActive = done && (idx === steps.length - 1 || !isStepCompleted(idx + 1));
                    const StepIcon = st.icon;

                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 2, width: '90px', textAlign: 'center' }}>
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: isCancelled && idx === steps.length - 1 ? '#ef4444' : done ? (isLastActive && currentStatus === 'delivered' ? '#16a34a' : '#ffffff') : '#ffffff',
                            border: `2px solid ${isCancelled && idx === steps.length - 1 ? '#ef4444' : done ? '#16a34a' : '#cbd5e1'}`,
                            color: isCancelled && idx === steps.length - 1 ? '#ffffff' : done ? (isLastActive && currentStatus === 'delivered' ? '#ffffff' : '#16a34a') : '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '8px',
                            boxShadow: done ? '0 2px 8px rgba(22, 163, 74, 0.15)' : 'none'
                          }}
                        >
                          <StepIcon size={18} />
                        </div>

                        <div style={{ fontSize: '12px', fontWeight: 800, color: isCancelled && idx === steps.length - 1 ? '#dc2626' : done ? (isLastActive ? '#16a34a' : '#0f172a') : '#64748b' }}>
                          {st.title}
                        </div>
                        <div style={{ fontSize: '10.5px', color: '#94a3b8', marginTop: '2px' }}>
                          {st.time}
                        </div>
                      </div>
                    );
                  })}

                </div>
              </div>

              {/* DYNAMIC STATUS BANNER BOX */}
              <div style={{ background: banner.bg, border: `1px solid ${banner.border}`, borderRadius: '14px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: banner.bg, border: `1px solid ${banner.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: banner.color, flexShrink: 0 }}>
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: 800, color: banner.color }}>
                    {banner.title}
                  </div>
                  <div style={{ fontSize: '12px', color: banner.subColor, marginTop: '2px' }}>
                    {banner.subtext}
                  </div>
                </div>
              </div>

            </div>

            {/* NEW: DYNAMIC REFUND LIFECYCLE TIMELINE CARD (IF CANCELLED) */}
            {isCancelled && (
              <div style={{ background: '#ffffff', border: '1px solid #fecaca', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 10px rgba(220,38,38,0.04)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#b91c1c', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RotateCcw size={18} color="#dc2626" />
                  <span>Refund & Cancellation Timeline</span>
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative', paddingLeft: '12px' }}>
                  {[
                    { stage: 'Cancellation Recorded', label: '1. Order Cancellation Recorded' },
                    { stage: 'Refund Requested', label: '2. Refund Request Automatically Logged' },
                    { stage: 'Refund Initiated', label: '3. Admin Approved & Refund Initiated' },
                    { stage: 'Refund Completed', label: '4. Refund Completed (£' + order.total_amount.toFixed(2) + ')' }
                  ].map((rStep, rIdx) => {
                    const currentRefundStatus = refundInfo?.refund_status || 'Refund Requested';
                    const isRefundDone = (
                      (rIdx === 0 && true) ||
                      (rIdx === 1 && true) || // Refund Requested is automatically logged when cancelled
                      (rIdx === 2 && (currentRefundStatus === 'Refund Initiated' || currentRefundStatus === 'Refund Completed')) ||
                      (rIdx === 3 && currentRefundStatus === 'Refund Completed')
                    );

                    const historyMatch = refundInfo?.history?.find(h => h.status === rStep.stage);

                    return (
                      <div key={rIdx} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: isRefundDone ? '#16a34a' : '#cbd5e1', marginTop: '3px', flexShrink: 0, boxShadow: isRefundDone ? '0 0 0 3px rgba(22, 163, 74, 0.2)' : 'none' }} />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: isRefundDone ? '#0f172a' : '#94a3b8' }}>
                            {rStep.label}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                            {historyMatch ? new Date(historyMatch.created_at).toLocaleString() : isRefundDone ? order.date_formatted : 'Pending'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 2. ORDER ITEMS LIST CARD MATCHING orderdetail.png 1:1 */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 20px 0' }}>
                Order Items ({order.items.length})
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {order.items.map((it, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', paddingBottom: idx < order.items.length - 1 ? '20px' : 0, borderBottom: idx < order.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ width: '64px', height: '64px', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', flexShrink: 0 }}>
                        <img src={it.image} alt={it.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      </div>
                      <div>
                        <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>{it.name}</h4>
                        {Array.isArray(it.selected_addons) && it.selected_addons.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '3px 0' }}>
                            {it.selected_addons.map((addon, aIdx) => (
                              <span key={aIdx} style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                                + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Qty: {it.quantity}</div>
                      </div>
                    </div>

                    <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>
                      £{it.price.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. DELIVERY INFORMATION CARD */}
            <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 20px 0' }}>
                Delivery Information
              </h3>

              <div className="order-delivery-info-grid">
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Delivered To</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>{recipientName}</div>
                  <div style={{ color: '#64748b', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {street}<br />
                    {city}, {postcode},<br />
                    {country}<br />
                    {phone}
                  </div>
                </div>

                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Delivery Method</div>
                    <div style={{ color: '#64748b' }}>Standard Shipping (Free)</div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                      {currentStatus === 'delivered' ? 'Delivered On' : currentStatus === 'shipped' ? 'Shipped On' : 'Order Placed On'}
                    </div>
                    <div style={{ color: '#64748b' }}>
                      {currentStatus === 'delivered' && getStepTimestamp('delivered') !== '--' ? getStepTimestamp('delivered') :
                       currentStatus === 'shipped' && getStepTimestamp('shipped') !== '--' ? getStepTimestamp('shipped') :
                       `${order.date_formatted} at ${order.time_formatted}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: ORDER SUMMARY, COUPON, PAYMENT, BILLING */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0 }}>
            
            {/* 1. ORDER SUMMARY CARD MATCHING orderdetail.png 1:1 */}
            <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 20px 0' }}>
                Order Summary
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#64748b', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Subtotal ({order.items.length} items)</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{order.subtotal.toFixed(2)}</span>
                </div>

                {/* Optional Add-Ons Summary Row if any items have add-ons */}
                {order.items.some(it => Array.isArray(it.selected_addons) && it.selected_addons.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '10px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Selected Upgrades & Add-Ons</div>
                    {order.items.flatMap(it => it.selected_addons || []).map((addon, aIdx) => (
                      <div key={aIdx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#991b1b', fontWeight: 700 }}>
                        <span>+ {addon.addon_name || addon.name}</span>
                        <span>+£{Number(addon.addon_price || addon.price || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Shipping</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{order.shipping_fee.toFixed(2)}</span>
                </div>

                {order.discount_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a', fontWeight: 700 }}>
                    <span>Discount ({order.coupon_code || 'SAVE10'})</span>
                    <span>- £{order.discount_amount.toFixed(2)}</span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Tax (20%)</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{order.tax_amount.toFixed(2)}</span>
                </div>
              </div>

              {/* Total Row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>Total</span>
                <span style={{ fontSize: '22px', fontWeight: 900, color: '#dc2626' }}>
                  £{order.total_amount.toFixed(2)}
                </span>
              </div>

              <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                {order.payment_method_info}
              </div>
            </div>

            {/* 2. COUPON DETAILS CARD (ONLY WHEN REAL COUPON APPLIED) */}
            {order.discount_amount > 0 && order.coupon_code && (
              <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 16px 0' }}>
                  Coupon Details
                </h3>

                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Tag size={18} color="#16a34a" />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>{order.coupon_code}</div>
                      <div style={{ fontSize: '11.5px', color: '#64748b' }}>£{order.discount_amount.toFixed(2)} off</div>
                    </div>
                  </div>

                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>
                    Applied
                  </span>
                </div>
              </div>
            )}

            {/* 3. PAYMENT DETAILS CARD MATCHING orderdetail.png 1:1 */}
            <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 16px 0' }}>
                Payment Details
              </h3>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '40px', height: '26px', borderRadius: '4px', background: '#1e3a8a', color: '#ffffff', fontWeight: 900, fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', letterSpacing: '0.05em' }}>
                    VISA
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>Visa ending in 4242</div>
                    <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>Exp 04/27</div>
                  </div>
                </div>

                <div style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a' }}>
                  £{order.total_amount.toFixed(2)}
                </div>
              </div>

              <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                Paid on {order.date_formatted} at {order.time_formatted}
              </div>
            </div>

            {/* 4. BILLING ADDRESS CARD MATCHING orderdetail.png 1:1 */}
            <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 12px 0' }}>
                Billing Address
              </h3>

              {(() => {
                const billing = order.billing_address || order.shipping_address || {};
                const bName = billing.full_name || billing.name || recipientName;
                const bStreet = billing.street_address || billing.street || street;
                const bCity = billing.city || city;
                const bPostcode = billing.postal_code || billing.postcode || postcode;
                const bCountry = billing.country || country;

                return (
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    <strong style={{ color: '#0f172a', display: 'block', marginBottom: '4px' }}>{bName}</strong>
                    {bStreet}<br />
                    {bCity}, {bPostcode},<br />
                    {bCountry}
                  </div>
                );
              })()}
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default UserOrderDetail;
